import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../utils/cn';

const LEAGUES = [
    { id: 'wooden', nameKey: 'league_wooden', icon: '/assets/league_wooden.png' },
    { id: 'bronze', nameKey: 'league_bronze', icon: '/assets/league_bronze.png' },
    { id: 'silver', nameKey: 'league_silver', icon: '/assets/league_silver.png' },
    { id: 'gold', nameKey: 'league_gold', icon: '/assets/league_gold.png' },
];

export const ProfileScreen = ({ onBack }) => {
    const { t } = useLanguage();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const tg = window.Telegram?.WebApp;
    const tgUser = tg?.initDataUnsafe?.user;

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const profile = await apiClient.getUserProfile();
                setUserProfile(profile);
            } catch (error) {
                console.error('Failed to load user profile:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-4">
                <div className="w-10 h-10 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
                <p className="text-sm text-slate-500 animate-pulse">{t('loading')}</p>
            </div>
        );
    }

    const leagueKey = userProfile?.league?.toLowerCase() || 'bronze';
    const league = LEAGUES.find(l => l.id === leagueKey) || LEAGUES[1];
    const stats = userProfile?.stats || { games_played: 0, wins: 0, losses: 0, win_rate: 0 };

    return (
        <div className="flex flex-col h-full w-full px-6 pt-6 pb-6 gap-6 relative overflow-hidden">
            {/* Ambient Background - Static & Stable */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-[-20%] left-[-20%] w-[140%] h-[140%] bg-[radial-gradient(circle_at_50%_50%,rgba(6,182,212,0.08),transparent_70%)]" />
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03]" />
            </div>

            {/* Header */}
            <div className="flex items-center gap-4 shrink-0 relative z-10">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={onBack}
                    className="w-10 h-10 p-0 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </Button>
                <h1 className="text-lg font-bold text-white tracking-widest uppercase">{t('profile_title')}</h1>
            </div>

            {/* Main Content Scrollable Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar relative z-10 pb-4">

                {/* Profile Card */}
                <div className="relative mb-6">
                    {/* Glow behind card */}
                    <div className="absolute inset-4 bg-cyan-500/20 blur-3xl rounded-full" />

                    <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 flex flex-col items-center gap-6 shadow-2xl">

                        {/* Avatar Container */}
                        <div className="relative">
                            {/* Subtle pulsing glow ring instead of rotating mess */}
                            <div className="absolute inset-0 rounded-full bg-cyan-500/20 animate-pulse blur-md" />
                            <div className="w-24 h-24 rounded-full p-[2px] bg-gradient-to-b from-cyan-500 to-slate-800 relative z-10">
                                <div className="w-full h-full rounded-full overflow-hidden bg-slate-900 border border-slate-800">
                                    <img
                                        src={tgUser?.photo_url || '/assets/human_head.png'}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>

                            {/* League Badge - Cleanly positioned */}
                            <div className="absolute -bottom-2 -right-2 w-9 h-9 bg-slate-800 rounded-full border border-white/10 flex items-center justify-center z-20 shadow-lg">
                                <img src={league.icon} alt="League" className="w-5 h-5 object-contain" />
                            </div>
                        </div>

                        {/* User Info */}
                        <div className="text-center space-y-1">
                            <h2 className="text-2xl font-bold text-white tracking-tight">{tgUser?.first_name || userProfile?.nickname}</h2>
                            {tgUser?.username && (
                                <p className="text-cyan-400 text-sm font-medium">@{tgUser.username}</p>
                            )}
                            <div className="pt-2">
                                <span className="inline-block px-3 py-1 rounded-lg bg-black/20 text-[10px] font-mono text-slate-500 border border-white/5">
                                    Telegram ID: {tgUser?.id || userProfile?.telegram_id}
                                </span>
                            </div>
                        </div>

                        {/* Rating & League Specs */}
                        <div className="grid grid-cols-2 gap-3 w-full">
                            <div className="bg-white/5 rounded-2xl p-3 border border-white/5 flex flex-col items-center">
                                <span className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-1">{t('rating')}</span>
                                <span className="text-lg font-bold text-white">🏆 {userProfile?.rating}</span>
                            </div>
                            <div className="bg-white/5 rounded-2xl p-3 border border-white/5 flex flex-col items-center">
                                <span className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-1">{t('league_title')}</span>
                                <span className="text-lg font-bold text-cyan-400 uppercase">{t(`league_${leagueKey}`)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="h-px flex-1 bg-white/10" />
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{t('profile_stats')}</span>
                        <div className="h-px flex-1 bg-white/10" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <StatItem label={t('profile_games')} value={stats.games_played} icon="🎮" />
                        <StatItem label={t('profile_win_rate')} value={`${stats.win_rate}%`} icon="⚡" highlight />
                        <StatItem label={t('profile_wins')} value={stats.wins} icon="💎" color="text-emerald-400" />
                        <StatItem label={t('profile_losses')} value={stats.losses} icon="💀" color="text-rose-400" />
                    </div>
                </div>

            </div>
        </div>
    );
};

const StatItem = ({ label, value, icon, highlight, color = "text-white" }) => (
    <div className={cn(
        "p-4 rounded-2xl border flex flex-col justify-between h-24 relative overflow-hidden transition-colors",
        highlight
            ? "bg-cyan-950/20 border-cyan-500/20"
            : "bg-white/5 border-white/5 hover:bg-white/10"
    )}>
        {highlight && (
            <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-500/10 rounded-bl-[100px] pointer-events-none" />
        )}

        <div className="flex justify-between items-start">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
            <span className="text-base opacity-50">{icon}</span>
        </div>

        <div className={cn("text-2xl font-bold font-mono tracking-tight", color)}>
            {value}
        </div>
    </div>
);
